'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { useCompetitionLabels } from '@/components/social/competition/competition-labels';
import { competitionRankingMetric, type TradingCompetitionSummary } from '@/components/social/competition/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Calendar03Icon, ChampionIconSolid, DollarCircleIcon, UserMultiple02IconSolid } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { formatCompetitionWindow } from '@/lib/competitions/format';
import { useFormattedCurrency } from '@/lib/currency';
interface CompetitionAboutSheetProps {
  competition: TradingCompetitionSummary;
  timeZone: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
const CASH_PROFIT_EXAMPLE = {
  cashInD6: '192638820000',
  cashOutD6: '168924570000',
  pumpProfitD6: '26638690000',
  cashProfitD6: '-2924440000'
} as const;
const CASH_PROFIT_PUMP_THRESHOLD_D6 = '1000000000';
const CASH_PROFIT_DAILY_BUDGET_D6 = '1000000000';
const CASH_PROFIT_SEVEN_DAY_BUDGET_D6 = '7000000000';

// The only place the rules, exact times and prize are written out in full; the
// hero and standings never repeat them.
export function CompetitionAboutSheet(t0) {
  const $ = _c(63);
  const {
    competition,
    timeZone,
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("competitions.about");
  const locale = useLocale();
  const labels = useCompetitionLabels();
  const {
    formatD6
  } = useFormattedCurrency();
  const explainsCashProfit = competition.rankingSource === "cash_profit";
  const [activeView, setActiveView] = useState("overview");
  let t1;
  if ($[0] !== t) {
    t1 = t("rankedBy");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== competition.board || $[3] !== labels) {
    t2 = labels.board(competition.board);
    $[2] = competition.board;
    $[3] = labels;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== competition || $[6] !== labels) {
    t3 = labels.metric(competitionRankingMetric(competition));
    $[5] = competition;
    $[6] = labels;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = `${t2} · ${t3}`;
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <InfoModal.IconItem icon={ChampionIconSolid} title={t1} description={t4} />;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("window");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== competition || $[14] !== locale || $[15] !== timeZone) {
    t7 = formatCompetitionWindow(competition, locale, timeZone);
    $[13] = competition;
    $[14] = locale;
    $[15] = timeZone;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <InfoModal.IconItem icon={Calendar03Icon} title={t6} description={t7} />;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t("prizePool");
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== competition.prizePoolD6 || $[23] !== formatD6) {
    t10 = formatD6(competition.prizePoolD6, "full");
    $[22] = competition.prizePoolD6;
    $[23] = formatD6;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <InfoModal.IconItem icon={DollarCircleIcon} title={t9} description={t10} />;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = t("entry");
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = t("entryAutomatic");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t12 || $[33] !== t13) {
    t14 = <InfoModal.IconItem icon={UserMultiple02IconSolid} title={t12} description={t13} />;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t11 || $[36] !== t14 || $[37] !== t5 || $[38] !== t8) {
    t15 = <InfoModal.IconList>{t5}{t8}{t11}{t14}</InfoModal.IconList>;
    $[35] = t11;
    $[36] = t14;
    $[37] = t5;
    $[38] = t8;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== competition.rankingSource || $[41] !== t) {
    t16 = competition.rankingSource === "board" ? t("noteBoard") : t("noteAdjusted");
    $[40] = competition.rankingSource;
    $[41] = t;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t16) {
    t17 = <p className="text-caption text-foreground-tertiary">{t16}</p>;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t15 || $[46] !== t17) {
    t18 = <>{t15}{t17}</>;
    $[45] = t15;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const overview = t18;
  let t19;
  if ($[48] !== t) {
    t19 = t("title");
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const t20 = explainsCashProfit ? "lg" : "default";
  let t21;
  if ($[50] !== activeView || $[51] !== explainsCashProfit || $[52] !== formatD6 || $[53] !== overview || $[54] !== t) {
    t21 = explainsCashProfit ? <Tabs value={activeView} onValueChange={setActiveView}><TabsList variant="segmented" size="md"><TabsTrigger value="overview">{t("overviewTab")}</TabsTrigger><TabsTrigger value="calculation">{t("calculationTab")}</TabsTrigger></TabsList><MotionAnimatedAutoHeight layoutDependencies={[activeView]}><TabsContent value="overview" className="flex flex-col gap-4 pt-4">{overview}</TabsContent><TabsContent value="calculation" className="flex flex-col gap-4 pt-4"><div className="flex flex-col gap-1"><h3 className="text-heading-xs text-foreground-primary">{t("cashProfitTitle")}</h3><p className="text-body text-foreground-secondary">{t("cashProfitIntro")}</p></div><InfoModal.Steps><InfoModal.Step title={t("cashProfitReceivedTitle")}>{t("cashProfitReceivedDescription")}</InfoModal.Step><InfoModal.Step title={t("cashProfitSpentTitle")}>{t("cashProfitSpentDescription")}</InfoModal.Step><InfoModal.Step title={t("cashProfitPumpTitle")}>{t("cashProfitPumpDescription", {
                pumpThreshold: formatD6(CASH_PROFIT_PUMP_THRESHOLD_D6, "full")
              })}</InfoModal.Step><InfoModal.Step title={t("cashProfitScalingTitle")}>{t("cashProfitScalingDescription", {
                dailyBudget: formatD6(CASH_PROFIT_DAILY_BUDGET_D6, "full"),
                sevenDayBudget: formatD6(CASH_PROFIT_SEVEN_DAY_BUDGET_D6, "full")
              })}</InfoModal.Step></InfoModal.Steps><InfoModal.Callout>{t("cashProfitExample", {
              cashIn: formatD6(CASH_PROFIT_EXAMPLE.cashInD6, "full"),
              cashOut: formatD6(CASH_PROFIT_EXAMPLE.cashOutD6, "full"),
              pumpProfit: formatD6(CASH_PROFIT_EXAMPLE.pumpProfitD6, "full"),
              cashProfit: formatD6(CASH_PROFIT_EXAMPLE.cashProfitD6, "full")
            })}{" "}{t("cashProfitRealizedDifference")}</InfoModal.Callout></TabsContent></MotionAnimatedAutoHeight></Tabs> : overview;
    $[50] = activeView;
    $[51] = explainsCashProfit;
    $[52] = formatD6;
    $[53] = overview;
    $[54] = t;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== competition.title || $[57] !== onOpenChange || $[58] !== open || $[59] !== t19 || $[60] !== t20 || $[61] !== t21) {
    t22 = <InfoModal open={open} onOpenChange={onOpenChange} title={t19} description={competition.title} size={t20}>{t21}</InfoModal>;
    $[56] = competition.title;
    $[57] = onOpenChange;
    $[58] = open;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  return t22;
}